import { AppInstallOption } from '@wix/editor-platform-host-integration-apis';
import { reportBi } from '../../../../essentials';
import { getGUID } from '../../../utils/guid';
import { BI_EVENT_IDS } from './constants';
import { UnifiedComponentsError } from './errors';
import { PlatformContext } from '../../../../types/platformApi';
import { findLast } from 'lodash';

type ExtraDataForBIsMap = {
  NAVIGATE_TO_DEV_CENTER: { appDefinitionId: string };
  NEW_COMPONENTS_INSTALL: {
    appDefinitionId: string;
    components_list: any[];
    numberInstalledComponents: number;
  };
  INSTALL_CODE_PACKAGES: {
    appDefinitionId: string;
  };
  PLATFORM_RUN_SCRIPTS: {
    appDefinitionId: string;
  };
  PLATFORM_ERROR: {
    errorCode: string;
    errorData: string;
    errorDetails: string;
    errorMessage: string;
    errorType: string;
  };
};

type ExtraDataForEvent<T extends keyof typeof BI_EVENT_IDS> =
  T extends keyof ExtraDataForBIsMap ? ExtraDataForBIsMap[T] : {};

type WithBI = <T extends keyof typeof BI_EVENT_IDS, V>(
  biEvent: T,
  extra: ExtraDataForEvent<T>,
  func: () => Promise<V>,
) => Promise<V>;

type ReportBI = <T extends keyof typeof BI_EVENT_IDS>(
  biEvent: T,
  extra: ExtraDataForEvent<T>,
) => void;

export type BIReporter = {
  withBI: WithBI;
  reportBI: ReportBI;
};

export function getBIReporter(
  context: PlatformContext,
  appDefIds: string[],
  options?: Record<string, AppInstallOption>,
): BIReporter {
  const installationId = getGUID();
  const editorType = window.commonConfig?.host?.toLowerCase();
  const fallbackAppDefIdForOrigin = findLast(
    appDefIds,
    (appDefId: string) => options?.[appDefId]?.origin,
  );

  const getOriginInfo = (appDefinitionId?: string): string => {
    if (appDefinitionId) {
      const appOptions = options?.[appDefinitionId];
      if (appOptions) {
        const originByOptions =
          appOptions.origin?.info?.type ||
          appOptions.origin?.info?.appDefinitionId;

        if (originByOptions) {
          return originByOptions;
        }
      }
    }

    if (
      fallbackAppDefIdForOrigin &&
      appDefinitionId !== fallbackAppDefIdForOrigin
    ) {
      return getOriginInfo(fallbackAppDefIdForOrigin as string);
    }

    return '';
  };

  const getAppDefinitionIdFromExtra = (extra?: any) =>
    Array.isArray(extra?.appDefinitionId)
      ? extra?.appDefinitionId[extra?.appDefinitionId.length - 1]
      : extra?.appDefinitionId;

  const reportProcess = <V>(
    evid: number,
    func: () => Promise<V>,
    extra?: any,
  ) => {
    const appDefinitionId = getAppDefinitionIdFromExtra(extra);

    const extendedExtra = {
      ...extra,
      ...context.intentInstallServices.getBIExtras({
        appDefinitionId: getAppDefinitionIdFromExtra(extra),
      }),
    };

    const startTime = Date.now();
    let endStatus = 'end';
    const origin = getOriginInfo(appDefinitionId);

    reportEvent(evid, {
      duration: 0,
      type: 'start',
      origin,
      ...extendedExtra,
    });
    return new Promise<V>((resolve, reject) => {
      func()
        .then(resolve)
        .catch((error) => {
          reportEvent(
            BI_EVENT_IDS.PLATFORM_ERROR,
            (error as UnifiedComponentsError)?.getExtraDetailsForBI?.(),
          );
          reject(error);
          endStatus = 'error';
        })
        .finally(() => {
          reportEvent(evid, {
            duration: Date.now() - startTime,
            type: endStatus,
            origin,
            ...extendedExtra,
          });
        });
    });
  };

  const reportEvent = (evid: number, extra?: any) => {
    const extendedExtra = {
      ...extra,
      ...context.intentInstallServices.getBIExtras({
        appDefinitionId: getAppDefinitionIdFromExtra(extra),
      }),
    };

    reportBi(evid, {
      installationId,
      editorType,
      ...extendedExtra,
    });
  };

  return {
    withBI: <T extends keyof typeof BI_EVENT_IDS, V>(
      biEvent: T,
      extra: ExtraDataForEvent<T>,
      func: () => Promise<V>,
    ): Promise<V> => {
      const evid = BI_EVENT_IDS[biEvent];
      switch (biEvent) {
        case 'PLATFORM_INSTALLATION':
          return reportProcess(evid, func, { appDefinitionId: appDefIds });
        case 'PROVISION_APPS':
          return reportProcess(evid, func, {
            appDefinitionId: appDefIds,
            name: func.name,
          });
        case 'NEW_COMPONENTS_INSTALL':
          const { components_list } =
            extra as ExtraDataForEvent<'NEW_COMPONENTS_INSTALL'>;
          return reportProcess(evid, func, {
            ...extra,
            numberTotalComponents: components_list.length,
            components_list: JSON.stringify(components_list),
            name: func.name,
          });
        default:
          return reportProcess(evid, func, { ...extra, name: func.name });
      }
    },
    reportBI: <T extends keyof typeof BI_EVENT_IDS>(
      biEvent: T,
      extra: ExtraDataForEvent<T>,
    ) => {
      // Currently relevant only to NAVIGATE_TO_DEV_CENTER event.
      // If you add another event - please use similar conditions structure as in `withBI`
      // to provide the relevant extras which should not be given by the user
      const evid = BI_EVENT_IDS[biEvent];
      return reportEvent(evid, extra);
    },
  };
}
